export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  description?: string;
  badge?: number;
  /** Sidebar group heading the item sits under. */
  section?: string;
  /** Section root used for the active state when `path` is a default child page. */
  matchPath?: string;
  children?: NavigationItem[];
}

export interface NavigationSection {
  id: string;
  label: string;
  items: NavigationItem[];
}

/**
 * Platform-level navigation only.
 * Project-scoped surfaces (Mission Control, Executions, etc.) live inside a project.
 */
export const orgNavigation: NavigationItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    path: '/overview',
    icon: 'overview',
    section: 'Workspace',
    description: 'Platform health across EnsoMirai++',
  },
  {
    id: 'projects',
    label: 'Projects',
    path: '/projects/all',
    icon: 'projects',
    section: 'Workspace',
    description: 'Choose a repository workspace',
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    path: '/monitoring',
    icon: 'monitoring',
    section: 'Workspace',
    description: 'Are scheduled jobs reporting in on time',
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: 'settings',
    section: 'System',
    description: 'Global system configuration',
  },
];

/**
 * Project workspace — Overview → Discover → Understand → Operate. Each entry
 * links straight to its section's default page (no index-route redirect, so no
 * extra hop or chunk round-trip); `matchPath` keeps the whole section active.
 */
export const getProjectNavigation = (projectId: string): NavigationItem[] => [
  {
    id: 'home',
    label: 'Overview',
    path: `/projects/${projectId}`,
    icon: 'overview',
    description: 'Project overview and lifecycle status',
  },
  {
    id: 'discover',
    label: 'Discover',
    path: `/projects/${projectId}/phase-0/repository`,
    matchPath: `/projects/${projectId}/phase-0`,
    icon: 'discover',
    section: 'Phases',
    description: 'Repository ingestion and discovery',
  },
  {
    id: 'understand',
    label: 'Understand',
    path: `/projects/${projectId}/phase-1/insights`,
    matchPath: `/projects/${projectId}/phase-1`,
    icon: 'understand',
    section: 'Phases',
    description: 'Architecture, graph, and AI reasoning',
  },
  {
    id: 'operate',
    label: 'Operate',
    path: `/projects/${projectId}/operate/incidents`,
    matchPath: `/projects/${projectId}/operate`,
    icon: 'operate',
    section: 'Phases',
    description: 'Monitor → Analyze → Execute → Assure → Improve',
  },
];

export const navigationSections: NavigationSection[] = [
  { id: 'org', label: 'Organization', items: orgNavigation },
];

/** Depth-first flatten of section items (including children). */
export function flattenNavigationItems(sections: NavigationSection[]): NavigationItem[] {
  const out: NavigationItem[] = [];
  const walk = (items: NavigationItem[]) => {
    for (const item of items) {
      out.push(item);
      if (item.children?.length) walk(item.children);
    }
  };
  for (const section of sections) walk(section.items);
  return out;
}

export const isNavigationItemActive = (item: NavigationItem, pathname: string) => {
  if (item.id === 'home') {
    return pathname === item.path || pathname === `${item.path}/`;
  }
  if (item.id === 'overview') {
    return pathname === '/' || pathname === '/overview' || pathname.startsWith('/overview/');
  }
  if (item.id === 'projects') {
    // The entry links to /projects/all, but every project surface
    // (/projects/active, /projects/:id/...) belongs to it.
    return pathname === '/projects' || pathname.startsWith('/projects/');
  }
  const base = item.matchPath ?? item.path;
  return pathname === base || pathname.startsWith(`${base}/`);
};

export const findNavigationItemById = (id: string): NavigationItem | undefined => {
  return orgNavigation.find((item) => item.id === id);
};

export const findNavigationItemByPath = (pathname: string): NavigationItem | undefined => {
  return orgNavigation.find((item) => isNavigationItemActive(item, pathname));
};

export const getNavigationChain = (pathname: string): NavigationItem[] => {
  const item = findNavigationItemByPath(pathname);
  return item ? [item] : [];
};
